import invariant from 'tiny-invariant';
import {
  DoneInvokeEvent,
  Machine,
  MachineConfig,
  MachineOptions,
  assign,
} from 'xstate';

import { client as apolloClient } from 'src/shared/graphql';
import { Job } from 'src/shared/graphql/generated';
import { queries } from 'src/shared/graphql/requests';

interface Context {
  jobId: number | null;
  loadingAnimationDone?: boolean;
  buildingLoaded?: boolean;
}

type Schema = {
  states: {
    loading: {};
    idle: {};
    notFound: {};
  };
};

interface FinishLoadingAnimationEvent {
  type: 'FINISH_LOADING_ANIMATION';
}

interface FinishLoadingBuildingEvent {
  type: 'FINISH_LOADING_BUILDING';
}

type Event = FinishLoadingAnimationEvent | FinishLoadingBuildingEvent;

const options: Pick<
  MachineOptions<Context, Event>,
  'actions' | 'guards' | 'services'
> = {
  actions: {
    assignBuildingLoaded: assign<Context, Event>({
      buildingLoaded: true,
    }),
    assignLoadingAnimationDone: assign<Context, Event>({
      loadingAnimationDone: true,
    }),
  },
  guards: {
    jobNotFound: (_ctx, evt: any) => {
      const event = evt as DoneInvokeEvent<Job | undefined>;
      return event.data === undefined;
    },
    finishedLoading: ctx => !!ctx.loadingAnimationDone && !!ctx.buildingLoaded,
  },
  services: {
    fetchJob: async ctx => {
      invariant(ctx.jobId, 'Missing jobId - That is sus dude');
      const jobResponse = await queries.getJob(apolloClient, { id: ctx.jobId });
      return jobResponse.data.jobs.results[0];
    },
  },
};

const machineConfig: MachineConfig<Context, Schema, Event> = {
  id: 'history',
  context: {
    jobId: null,
    loadingAnimationDone: false,
    buildingLoaded: false,
  },
  initial: 'loading',
  states: {
    loading: {
      invoke: {
        src: 'fetchJob',
        onDone: [
          {
            target: 'notFound',
            cond: 'jobNotFound',
          },
        ],
      },
      always: {
        target: 'idle',
        cond: 'finishedLoading',
      },
      on: {
        FINISH_LOADING_ANIMATION: [{ actions: 'assignLoadingAnimationDone' }],
        FINISH_LOADING_BUILDING: [{ actions: 'assignBuildingLoaded' }],
      },
    },
    idle: {},
    notFound: {},
  },
};

const createMachine = (initialContext: Context) =>
  Machine<Context, Schema, Event>(machineConfig, options, initialContext);

export { Context, Event, machineConfig, options, createMachine };
